// React Query hooks for customers. Thin wrappers over lib/api/customers.ts.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listCustomers,
  getCustomer,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  setCustomerActive,
  setCustomersActive,
  hardDeleteUnusedCustomers,
  searchCustomers,
  type CreateCustomerInput,
  type UpdateCustomerInput,
  type ListCustomersOptions,
  type SearchCustomersOptions,
  importCustomers,
  type ImportCustomerRow,
} from '@/lib/api/customers';

const KEYS = {
  all: ['customers'] as const,
  list: (filters?: ListCustomersOptions) => [...KEYS.all, 'list', filters ?? {}] as const,
  detail: (id: string) => [...KEYS.all, 'detail', id] as const,
  search: (term: string, opts?: SearchCustomersOptions) =>
    [...KEYS.all, 'search', term, opts ?? {}] as const,
};

/** List all customers (optionally filtered by isActive). */
export const useCustomers = (filters?: ListCustomersOptions) =>
  useQuery({
    queryKey: KEYS.list(filters),
    queryFn: () => listCustomers(filters),
  });

/** Get a single customer by id. Pass null/undefined to disable. */
export const useCustomer = (id: string | null | undefined) =>
  useQuery({
    queryKey: KEYS.detail(id ?? ''),
    queryFn: () => (id ? getCustomer(id) : Promise.resolve(null)),
    enabled: !!id,
  });

/**
 * Search customers by name/company/phone/email for the POS picker.
 * Caller controls the debounce and the `enabled` flag.
 * Skipped automatically when `term` is blank.
 */
export const useSearchCustomers = (
  term: string,
  opts?: Omit<SearchCustomersOptions, 'term'> & { enabled?: boolean },
) =>
  useQuery({
    queryKey: KEYS.search(term, opts as never),
    queryFn: () => searchCustomers(term, opts),
    enabled: (opts?.enabled ?? true) && term.trim().length > 0,
    staleTime: 10 * 1000,
  });

export const useCreateCustomer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCustomerInput) => createCustomer(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
};

export const useUpdateCustomer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateCustomerInput }) =>
      updateCustomer(id, input),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.id) });
    },
  });
};

/** Soft-delete: sets isActive=false. */
export const useDeleteCustomer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCustomer(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
};

/** Reactivate or deactivate a customer. */
export const useSetCustomerActive = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      setCustomerActive(id, isActive),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
};

/**
 * Bulk import. Invalidates the whole customer tree rather than appending: the
 * import can create hundreds of rows and skip others, so the counts and lists
 * everywhere are stale in ways a targeted patch would not catch.
 */
export const useImportCustomers = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rows: ImportCustomerRow[]) => importCustomers(rows),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
};

/**
 * Bulk deactivate or reactivate the checkbox selection on the Customers page.
 * Invalidates every customer query: a touched row can sit in a list, a detail
 * view or a picker's search results. On settle rather than success -- the
 * writes go out in chunks, so a failure can arrive after some already landed.
 */
export const useSetCustomersActive = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      setCustomersActive(ids, isActive),
    onSettled: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
};

/** Bulk permanent delete of whatever part of the Customers selection nothing references. */
export const useHardDeleteUnusedCustomers = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => hardDeleteUnusedCustomers(ids),
    // Settled, for the same reason as above: the chunks delete independently.
    onSettled: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
};
